import http from '@/http'
import { computed, ComputedRef, readonly, ref, toRaw } from 'vue'
import { useFetch } from './useFetchContent'
import { useStore } from 'vuex'
import { useSearch } from './useSearch'
import { useWidgetNavigator } from './useWidgetNavigator'
import { useWidgetToc } from './useWidgetToc'
import { useWidgetEvents } from './useWidgetEvents'

import {
  LibraryWidgetPages,
  MediaWidgetEntityTypes,
  ProductPageWidgetPages,
  TocFlatListItemDto,
  WidgetErrorPages,
  WidgetEventErrors,
  WidgetMediaTypes,
  WidgetRootBreadcrumbs,
  WidgetTypes
} from '@/interfaces/global/widgets'

import { ContentDto, PageDtoExtended, TocFlatListDto } from '@/interfaces/global'

import {
  getMediaById,
  getMediaByIdentifier,
  getPage,
  getPageByHashKey,
  getProductPageByIdentifier
} from '@/controllers/library'
import { useBrowseFlows } from '@/components/widgets/composables/useBrowseFlows'

const tocQuery = ref('')
const isSelectingBomItemViaWidgetConfig = ref(false)

export function useOpenToContent() {
  const store = useStore()

  const { goToContentInBook } = useWidgetToc()
  const { emitLoginEvent, emitErrorEvent } = useWidgetEvents()
  const { navigateToBrowse, setTaxonIdList } = useBrowseFlows()
  const { navigateToSearch } = useSearch()
  const { setErrorFetchingContent } = useFetch()

  const {
    currentWidgetView,
    isBook,
    media,
    page,
    pagePath,
    setMedia,
    setCurrentWidgetView,
    setRootBreadcrumb,
    navigateToContent,
    navToViewWithLoading,
    setPageHashkey,
    clearAllContentState,
    setParentChapters,
    setProductPage
  } = useWidgetNavigator()

  let landingPage: string|undefined
  let isSettingLandingPage = false

  const widgetType: ComputedRef<WidgetTypes> = computed(
    () => store.getters['widgets/widgetType']
  )
  const browseFlowId = computed(() => {
    return store.getters['widgets/browseFlowNodeId']
  })
  const mediaIdentifier = computed(() => {
    return store.getters['widgets/mediaIdentifier'];
  })
  const pageHashKey = computed(() => {
    return store.getters['widgets/pageHashKey']
  })
  const partNumber = computed(() => {
    return store.getters['widgets/partNumber']
  })
  const productPageIdentifier = computed(() => {
    return store.getters['widgets/productPageIdentifier']
  })
  const hasContentPath = computed(() => {
    const hasPageOrPart = !!pageHashKey.value
      || !!partNumber.value

    switch(widgetType.value) {
      case WidgetTypes.library:
        return !!browseFlowId.value || !!mediaIdentifier.value || hasPageOrPart
      case WidgetTypes.media:
        return hasPageOrPart
      default:
        return false
    }
  })
  const tocFlatList = computed(() => {
    return store.getters['content/widgetTocFlatList']
  })

  const tocFlatMap = computed(() => {
    return store.getters['content/widgetTocFlatMap']
  })

  const widgetsWithHomeBreadcrumb = computed(() => {
    return new Set([ WidgetTypes.library ])
  })

  const configHasPageAndPart = computed(() => {
    return !!pageHashKey.value && !!partNumber.value
  })

  const hasWidgetTocEnabled = computed(() => {
    return store.getters['widgets/showToc']
  })

  const willPreFetchBomItems = computed(() => {
    return !!media.value
      && configHasPageAndPart.value
      && isSettingLandingPage
  })

  function classifyError(
    error: unknown,
    fallback: WidgetEventErrors = WidgetEventErrors.config
  ): WidgetEventErrors {
    if (Object.values(WidgetEventErrors).includes(error as WidgetEventErrors)) {
      return error as WidgetEventErrors
    }
    const status = (error as any)?.response?.status
    return (status === 403 || status === 404)
      ? WidgetEventErrors.content_not_found
      : fallback
  }

  function setHomeBreadcrumb() {
    if (widgetsWithHomeBreadcrumb.value.has(widgetType.value)) {
      setRootBreadcrumb(WidgetRootBreadcrumbs.home)
    }
  }

  async function fetchAndSetMedia(identifier: string) {
    const media = await getMediaByIdentifier(identifier)
    setMedia(media)
    setHomeBreadcrumb()
    landingPage = LibraryWidgetPages.media
  }

  async function handleLibraryWidget() {
    if (!hasContentPath.value) {
      setCurrentWidgetView(LibraryWidgetPages.home)
    } else {
      await openToContentPath()
    }
  }

  async function openToContentPath() {
    try {
      if (hasMultipleWidgetConfigLocations()) {
        emitErrorEvent({ error: WidgetEventErrors.multiple_locations })
        return
      }

      /**
       * Resolution chain priority:
       * 1. browseFlowId (if present, it's the primary entry point)
       * 2. mediaIdentifier
       * 3. pageHashKey
       * 4. partNumber
       *
       * If the first configured entity fails, fall back to Home page.
       * Subsequent entities should not navigate if the first fails.
       */
      let firstEntityResolved = false

      if (!!browseFlowId.value) {
        try {
          const breadcrumbResponse = await http.get(`browse/${browseFlowId.value}/breadcrumb`)
          const breadcrumbIds = breadcrumbResponse.data
            .map((dto: { id: number, name: string }) => dto.id)
          let displayOrder = 0

          if (breadcrumbIds.length > 2) {
            for (let i=1; i<breadcrumbIds.length-1; i++) {
              const taxonId = breadcrumbIds[i]
              const taxon = await http.get(`browse/${taxonId}`)

              setTaxonIdList({
                description: taxon.data.description ?? "",
                displayOrder: displayOrder++,
                hasChildren: taxon.data?.children?.length > 0 ?? false,
                id: taxonId,
                name: taxon.data.name ?? "",
                parentId: breadcrumbIds[i-1]
              })
            }
          }

          const { data } = await http.get(`browse/${browseFlowId.value}`)
          navigateToBrowse({
            description: data?.description ?? "",
            displayOrder: displayOrder++,
            hasChildren: data?.children?.length > 0 ?? false,
            id: data.id,
            name: data?.name ?? "",
            parentId: breadcrumbIds[breadcrumbIds.length - 2]
          })
          firstEntityResolved = true
        } catch(e) {
          emitErrorEvent({ error: WidgetEventErrors.location })
          return
        }
      }

      if (!!mediaIdentifier.value) {
        try {
          await fetchAndSetMedia(mediaIdentifier.value)
          firstEntityResolved = true
        } catch(e) {
          emitErrorEvent({ error: WidgetEventErrors.location })
          if (!firstEntityResolved) return
        }
      }

      if (!!pageHashKey.value) {
        try {
          await getContentForPage()
          firstEntityResolved = true
        } catch(e) {
          emitErrorEvent({ error: WidgetEventErrors.location })
          if (!firstEntityResolved) return
        }
      }

      if (!!partNumber.value) {
        try {
          await navigateToPartNumber()
        } catch(e) {
          emitErrorEvent({ error: WidgetEventErrors.location })
          if (!firstEntityResolved) return
        }
      }
    } catch(e: any) {
      // Unexpected errors - should rarely reach here
      emitErrorEvent({ error: classifyError(e) })
    } finally {
      if ((landingPage === LibraryWidgetPages.media)
        && (currentWidgetView.value !== LibraryWidgetPages.media)) {
        await navToViewWithLoading(media.value)
      } else if (!currentWidgetView.value) {
        setCurrentWidgetView(LibraryWidgetPages.home)
      }
      landingPage = undefined
    }
  }

  async function selectFirstOccurrenceInBom() {
    await fetchBomContentForVuex()
    const bomItem = store.state.content.bom.items
      .find((it: { partNumber: string }) => {
        return it.partNumber === partNumber.value
      })
    if (!bomItem) throw WidgetEventErrors.location

    // There is an existing bug affecting Bom.vue & bom.js module.
    // BOM_RECEIVED mutation too loosely assigns keys in itemsMap,
    // resulting in sometimes a key of 'undefined'.
    // This collides with a search query value of '', resulting
    // in a false match that auto-selects a BOM item on mount.
    // This state var supports an if check to block the bug from overriding
    // selection in the BOM of the provided partNumber.
    setIsSelectingBomItemViaWidgetConfig(true)
    await store.dispatch('content/selectBomItems', { item: bomItem })
    setTocQuery(partNumber.value)
  }

  async function navigateToPartNumber() {
    // When:
      // !!page -> select first occurrence of part in BOM
      // !!media && !page -> Book TOC search on part
      // !media && !page -> navigateToSearch & search on part
    if (!!page.value) {
      await selectFirstOccurrenceInBom()
    }
    else if (!!media.value && !page.value) {
      // Navigate to the book
      await navToViewWithLoading(media.value)
      // TableOfContents.vue doesn't finish loading toc on mount.
      // Consequently, it uses a watcher to call search
      // when the toc finishes loading. Setting this state here,
      // allows said watcher to use it to update the search query
      // param to the provided partNumber.
      // Attempts at pre-fetching TOC result so it's present on mount
      // resulted in race condition with follow-on navigation to BOM.
      setTocQuery(partNumber.value)
    }
    else if ((!media.value && !page.value)
      && (widgetType.value === WidgetTypes.library)) {
      navigateToSearch(partNumber.value)
    }
  }

  async function handleMediaWidget() {
    try {
      const mediaDto = await getMediaByIdentifier(mediaIdentifier.value)
      setMedia(mediaDto)

      if (!hasContentPath.value) {
        navToViewWithLoading(mediaDto)
      } else {
        let navigatedToPage = false

        if (!!pageHashKey.value) {
          try {
            await getContentForPage()
            navigatedToPage = true
          } catch(e) {
            emitErrorEvent({ error: WidgetEventErrors.location })
          }
        }

        if (!!partNumber.value) {
          try {
            await navigateToPartNumber()
          } catch(e) {
            emitErrorEvent({ error: WidgetEventErrors.location })
          }
        }

        if (!navigatedToPage) {
          navToViewWithLoading(mediaDto)
        }
      }
    } catch(error) {
      throw classifyError(error)
    }
  }

  async function getContentForPage() {
    try {
      const page = await getPageByHashKey(pageHashKey.value)
      if (isBook.value) {
        await fetchTocForVuex()
        const idx = getPageIndexInFlatList(page)
        if (idx >= 0) {
          const content = tocFlatList.value[idx]
          await goToContentInBook(content)
        } else {
          throw WidgetEventErrors.location
        }
      } else {
        navigateToContent(page)
      }
      if (widgetType.value === WidgetTypes.library) {
        setHomeBreadcrumb()
      }
      landingPage = LibraryWidgetPages.page
    } catch(e) {
      throw classifyError(e)
    }
  }

  async function handlePageWidget() {
    try {
      const page = await getPageByHashKey(pageHashKey.value)
      navigateToContent(page)
    } catch(error) {
      throw classifyError(error)
    }

    if (!!partNumber.value) {
      try {
        await selectFirstOccurrenceInBom()
      } catch(e) {
        emitErrorEvent({ error: WidgetEventErrors.location })
      }
    }
  }

  async function handleProductPageWidget() {
    try {
      const { data } = await getProductPageByIdentifier(productPageIdentifier.value)
      // Ensure the DTO has the type field set for template lookups and routing
      const productPageDto = { ...data, type: 'product-page' }
      // Set product page state - WidgetProductPage component will handle Vuex sync via watcher
      setProductPage(productPageDto)
      setCurrentWidgetView(ProductPageWidgetPages.productPage)
    } catch (e) {
      setCurrentWidgetView(WidgetErrorPages.contentError)
      throw classifyError(e)
    }
  }

  async function setLandingPage() {
    const handlerMap = new Map([
      [WidgetTypes.library, handleLibraryWidget],
      [WidgetTypes.media, handleMediaWidget],
      [WidgetTypes.page, handlePageWidget],
      [WidgetTypes.productPage, handleProductPageWidget],
    ])
    try {
      isSettingLandingPage = true
      const handler = handlerMap.get(widgetType.value)
      if (!handler) {
        setErrorFetchingContent(WidgetEventErrors.config)
        return
      }
      await handler()
      emitLoginEvent()
    } catch(error) {
      const classifiedError = classifyError(error)
      setErrorFetchingContent(classifiedError)

      if (classifiedError === WidgetEventErrors.location) {
        emitLoginEvent()
      }
      emitErrorEvent({ error: classifiedError })
    } finally {
      isSettingLandingPage = false
    }
  }

  async function fetchTocForVuex() {
    await store.dispatch('content/getTOC',
      { id: media.value?.id, type: 'book' })
  }

  async function fetchBomContentForVuex() {
    const params = {
      id: pagePath.value,
      type: isBook.value
        ? WidgetMediaTypes.book
        : MediaWidgetEntityTypes.page
    }
    await store.dispatch("content/getContent", params)
    if (pageHashKey.value !== store.state.content.pageHashkey) {
      setPageHashkey(store.state.content.pageHashkey)
    }
  }

  function getPageIndexInFlatList(page: PageDtoExtended) {
    return tocFlatList.value
      .findIndex(({ item: { type, id } }: TocFlatListDto) => {
        return (type === 'page') && (id === page.id)
      })
  }

  function setTocQuery(value: string) {
    //deprecated - use vuex store tocQuery instead
    if (hasWidgetTocEnabled.value) {
      tocQuery.value = value
    }
  }

  function setIsSelectingBomItemViaWidgetConfig(value: boolean) {
    isSelectingBomItemViaWidgetConfig.value = value
  }

  function getFirstMatchingIdxInBook({ type, id }: { type: string, id: number }) {
    const itemPath = `${type}:${id}`
    const tocKeys: string[] = [ ...tocFlatMap.value.keys() ]
    return tocKeys
      .findIndex((it: string) => it.includes(itemPath))
  }

  function hasHotpointLinkInBook(linkDto: { type: string, id: number }) {
    const idx = getFirstMatchingIdxInBook(linkDto)
    return ((linkDto.type === WidgetTypes.page as string) || (linkDto.type === 'chapter'))
      && (idx >= 0)
  }

  function hasMultipleWidgetConfigLocations() {
    const locations = [
      mediaIdentifier.value,
      pageHashKey.value,
      partNumber.value
    ]

    return !!browseFlowId.value && locations.filter((it) => !!it).length > 0
  }

  async function openToContentDecorator(fun: Function, id: number) {
    try {
      clearAllContentState()
      const contentDto = await fun(id)
      navigateToContent(contentDto)
    } catch {
      setErrorFetchingContent(WidgetEventErrors.content_not_found)
    }
  }

  async function openToChapter(id: number, mediaId: number) {
    const getMediaAndChapter = async () => {
      const mediaDto = await getMediaById(mediaId)
      setMedia(mediaDto)
      await fetchTocForVuex()
      return store.getters['content/widgetTocFlatList']
        .find((it: TocFlatListItemDto) => (it.item.type === 'chapter') && (it.item.id === id))
        .item
    }
    await openToContentDecorator(getMediaAndChapter, id)
    updateParentChaptersForChapter(id)
  }

  function updateParentChaptersForChapter(chapterId: number) {
    const chapterTocDto: TocFlatListItemDto|undefined = store.getters['content/widgetTocFlatList']
      .find((it: TocFlatListItemDto) => (it.item.id === chapterId) && (it.item.type === 'chapter'))
    const parentChapterIdList = Array.from(chapterTocDto?.chapters ?? [])
      .filter(id => id !== chapterId)
    const parentChapters: ContentDto[] = parentChapterIdList.map(id => {
      return toRaw(store.getters['content/widgetTocFlatList'].find((it: TocFlatListItemDto) => {
        return (it.item.id === id) && (it.item.type === 'chapter')
      }).item)
    })
    if (!!parentChapters.length) {
      setParentChapters(parentChapters)
    }
  }

  async function widgetOpenHotpointLink(link: { id: number, name: string, type: string, mediaId?: number }) {
    const authorizedWidgets = new Set([
      WidgetTypes.library,
      WidgetTypes.media
    ])

    if (!authorizedWidgets.has(widgetType.value)) return

    const firstMatchIdx = getFirstMatchingIdxInBook(link)
    if (firstMatchIdx >= 0) {
      const item:  TocFlatListItemDto = toRaw(tocFlatList.value[firstMatchIdx])
      await goToContentInBook(item)
    } else if (widgetType.value === WidgetTypes.library) {
      switch (link.type) {
        case MediaWidgetEntityTypes.media as string:
          await openToContentDecorator(getMediaById, link.id)
          break
        case MediaWidgetEntityTypes.page as string:
          await openToContentDecorator(getPage, link.id)
          break
        case MediaWidgetEntityTypes.chapter as string:
          if (!!link.mediaId && typeof link.mediaId === 'number') {
            await openToChapter(link.id, link.mediaId)
          }
      }
    }
  }

  return {
    tocQuery: readonly(tocQuery),
    isSelectingBomItemViaWidgetConfig: readonly(isSelectingBomItemViaWidgetConfig),
    configHasPageAndPart,
    willPreFetchBomItems,
    setTocQuery,
    setLandingPage,
    fetchBomContentForVuex,
    setIsSelectingBomItemViaWidgetConfig,
    widgetOpenHotpointLink,
    hasHotpointLinkInBook
   }
}
